import { activeCallOptions } from '@doneverse/data/queries'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useDashboardStore, type ActiveCallSession } from '@/store'

/**
 * Watches the call this device is on and hands anything the socket missed to
 * the store.
 *
 * Realtime events are at-most-once and the media connection outlives a dropped
 * socket, so a tab whose socket was down while the call ended — or while
 * another of the user's devices took the seat — would otherwise sit on a dead
 * dock with a live microphone. This re-reads the call over REST and lets
 * `reconcileSeat` decide; all the policy lives there, so the open thread and
 * this watcher can't reach different conclusions from the same read.
 *
 * Renders nothing, deliberately. It reads `dataUpdatedAt`, which changes on
 * every successful poll — inside CallHost that would re-render the dock and the
 * whole RealtimeKit surface beneath it every 30 seconds.
 */
export function CallSeatWatcher({ activeCall }: { activeCall: ActiveCallSession }) {
  const reconcileSeat = useDashboardStore((s) => s.reconcileSeat)
  const openChatContactId = useDashboardStore((s) => s.openChatContactId)

  // React Query installs `refetchInterval` per OBSERVER, not per query. The
  // open thread already polls this exact key, so claiming the interval here too
  // would double both the DB load and the RealtimeKit calls the endpoint's
  // reconciler makes. Poll only when nobody else is watching this conversation.
  const { data, dataUpdatedAt } = useQuery({
    ...activeCallOptions(activeCall.contactId),
    refetchInterval: openChatContactId === activeCall.contactId ? false : 30_000,
  })

  useEffect(() => {
    if (!data) return
    reconcileSeat(data.call, dataUpdatedAt)
  }, [data, dataUpdatedAt, reconcileSeat])

  return null
}
